<template>
  <div class="content mb-3">
    <div class="mb-5">
      <div>
        <h2>{{ this.$t("Cloud Key Details") }}</h2>
      </div>
      <b-form>
        <b-row>
          <b-col sm="12" md="12" lg="6">
            <b-form-group
              label-for="log-name-input"
              :label="this.$t('Log Name')"
              :state="$validateState($v.form.logname)"
            >
              <b-form-input
                data-test-id="cloud-keys--cloud-key--log-name"
                id="log-name-input"
                :placeholder="$t('e.g. IT Visitor')"
                v-model="$v.form.logname.$model"
                :state="$validateState($v.form.logname)"
              />
              <template #invalid-feedback>
                <p v-if="$v.form.logname.$dirty && !$v.form.logname.minLength">
                  {{
                    $t("X must be at least X characters long.", {
                      field: $t("Log name"),
                      length: $v.form.logname.$params.minLength.min,
                    })
                  }}
                </p>
                <p v-else-if="!$v.form.logname.required">
                  {{ $t("This is a required field.") }}
                </p>
              </template>
            </b-form-group>
          </b-col>
          <b-col sm="12" md="12" lg="6">
            <b-form-group
              label-for="key-id-input"
              :label="this.$t('Key')"
              :state="$validateState($v.form.keyid)"
            >
              <b-form-input
                data-test-id="cloud-keys--cloud-key--key-id-input"
                id="key-id-input"
                v-model="$v.form.keyid.$model"
                :state="$validateState($v.form.keyid)"
              />
              <template #invalid-feedback>
                <p v-if="$v.form.keyid.$dirty && !$v.form.keyid.minLength">
                  {{
                    $t("X must be at least X characters long.", {
                      field: $t("Key "),
                      length: $v.form.keyid.$params.minLength.min,
                    })
                  }}
                </p>
                <p v-else-if="!$v.form.keyid.required">
                  {{ $t("This is a required field.") }}
                </p>
              </template>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="12" md="6" lg="3">
            <b-form-group
              id="expires-after"
              label-for="expires-after-input"
              :label="this.$t('Expires After')"
              :state="$validateState($v.form.expiresafter)"
            >
              <b-form-input
                data-test-id="cloud-keys--cloud-key--expires-after-input"
                id="expires-after-input"
                type="number"
                :min="0"
                :placeholder="$t('Enter number')"
                v-model="$v.form.expiresafter.$model"
                :state="$validateState($v.form.expiresafter)"
              />
              <template #invalid-feedback>
                <p
                  v-if="
                    $v.form.expiresafter.$dirty &&
                    !$v.form.expiresafter.numeric &&
                    !$v.form.expiresafter.minValue
                  "
                >
                  {{ $t("Must be a positive integer") }}
                </p>
              </template>
            </b-form-group>
          </b-col>
          <b-col sm="12" md="6" lg="3">
            <b-form-group
              label-for="expires-after-period-input"
              :label="this.$t('Period')"
            >
              <b-form-select
                data-test-id="cloud-keys--cloud-key--expires-after-period-input"
                id="expires-after-period-input"
                v-model="form.expiresafterperiod"
                :options="expiresAfterPeriodOptions"
                :disabled="
                  !$v.form.expiresafter.$model ||
                  !($validateState($v.form.expiresafter) === null)
                "
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="6" lg="3">
            <b-form-group
              label-for="max-uses-input"
              :label="this.$t('Maximum Uses')"
              :state="$validateState($v.form.maxuse)"
            >
              <b-form-input
                data-test-id="cloud-keys--cloud-key--max-uses-input"
                id="max-uses-input"
                type="number"
                :min="0"
                :placeholder="$t('Enter number')"
                v-model="$v.form.maxuse.$model"
                :state="$validateState($v.form.maxuse)"
              />
              <template #invalid-feedback>
                <p
                  v-if="
                    $v.form.maxuse.$dirty &&
                    !$v.form.maxuse.numeric &&
                    !$v.form.maxuse.minValue
                  "
                >
                  {{ $t("Must be a positive integer") }}
                </p>
              </template>
            </b-form-group>
          </b-col>
          <b-col sm="12" md="12" lg="6">
            <b-form-group
              id="date-from"
              label-for="date-from-input"
              :label="this.$t('Valid From')"
            >
              <b-form-datepicker
                data-test-id="cloud-keys--cloud-key--valid-from-input"
                id="date-from-input"
                v-model="form.validfrom"
                :date-format-options="dateFormatOptions"
                :initial-date="defaultDate"
                :max="form.validto"
                reset-button
                :reset-value="defaultDate"
                @input="onInputChange"
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="12" lg="6">
            <b-form-group
              id="date-to"
              label-for="date-to-input"
              :label="this.$t('Valid To')"
            >
              <b-form-datepicker
                data-test-id="cloud-keys--cloud-key--valid-to-input"
                id="date-to-input"
                v-model="form.validto"
                :date-format-options="dateFormatOptions"
                :min="form.validfrom"
                reset-button
                @input="onInputChange"
              />
            </b-form-group>
          </b-col>
          <b-col sm="12" md="12" lg="6">
            <b-form-group
              id="status"
              label-for="status-input"
              :label="this.$t('Status')"
            >
              <b-form-select
                data-test-id="cloud-keys--cloud-key--status-input"
                id="active"
                :options="statusOptions"
                v-model="$v.form.active.$model"
              ></b-form-select>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col sm="12">
            <b-form-group
              label-for="comment-input"
              :label="$t('Description')"
              :state="$validateState($v.form.comment)"
            >
              <b-form-textarea
                data-test-id="cloud-keys--cloud-key--comment-input"
                id="comment-input"
                :placeholder="$t('e.g. Head Office')"
                no-resize
                v-model="$v.form.comment.$model"
                :state="$validateState($v.form.comment)"
              ></b-form-textarea>
              <template #invalid-feedback>
                <p v-if="$v.form.comment.$dirty && !$v.form.comment.minLength">
                  {{
                    $t("X must be at least X characters long.", {
                      field: $t("Comment"),
                      length: $v.form.comment.$params.minLength.min,
                    })
                  }}
                </p>
              </template>
            </b-form-group>
          </b-col>
        </b-row>
        <t-save-button
          data-test-id="cloud-keys--cloud-key--save"
          :form-model="$v.form"
          :fetching="fetching"
          @submit="onSubmit"
        />
      </b-form>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import dayjs from "dayjs";
import {
  required,
  integer,
  minLength,
  minValue,
} from "vuelidate/lib/validators";

export default {
  layout: "customer",
  data() {
    return {
      fetching: false,

      form: {
        logname: " ",
        keyid: " ",
        expiresafter: 0,
        expiresafterperiod: "hours",
        maxuse: 0,
        validfrom: null,
        validto: null,
        active: "true",
        comment: " ",
      },

      expiresAfterPeriodOptions: [
        {
          value: "days",
          text: this.$t("Days"),
        },
        {
          value: "hours",
          text: this.$t("Hours"),
        },
        {
          value: "mins",
          text: this.$t("Minutes"),
        },
      ],

      dateFormatOptions: {
        year: "numeric",
        month: "long",
        day: "numeric",
      },

      statusOptions: [
        {
          value: true,
          text: this.$t("Active"),
        },
        {
          value: false,
          text: this.$t("Inactive"),
        },
      ],
    };
  },

  computed: {
    ...mapGetters("authentication", ["selectedUser"]),
    ...mapGetters("cloudKeys", ["keys", "selectedCloudKey"]),

    defaultDate: () => dayjs().format("YYYY-MM-DD"),
  },

  methods: {
    ...mapActions("pageMeta", ["setPageTitle"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("cloudKeys", [
      "fetchCustomerCloudKeys",
      "fetchCustomerCloudKey",
      "updateCustomerCloudKey",
    ]),

    fromDate() {
      return this.form.validfrom;
    },

    onInputChange() {
      this.$v.$touch();
    },

    async onSubmit() {
      this.fetching = true;
      const body = { ...this.form };

      try {
        await this.updateCustomerCloudKey({
          id: this.selectedUser.id,
          cloudKeyId: this.$route.params.id,
          body,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetching = false;
        this.$v.form.$reset();
      }
    },
  },

  validations: {
    form: {
      logname: {
        required,
        minLength: minLength(5),
      },
      keyid: {
        required,
        minLength: minLength(5),
      },
      expiresafter: {
        integer,
        minValue: minValue(1),
      },
      maxuse: {
        integer,
        minValue: minValue(1),
      },
      comment: {
        minLength: minLength(5),
      },
      active: {},
    },
  },

  watch: {
    selectedCloudKey() {
      const {
        logname,
        keyid,
        expiresafter,
        expiresafterperiod,
        maxuse,
        validfrom,
        validto,
        active,
        comment,
      } = this.selectedCloudKey;

      this.form = {
        logname,
        keyid,
        expiresafter,
        expiresafterperiod,
        maxuse,
        validfrom,
        validto,
        active,
        comment,
      };
    },
  },
  async created() {
    this.setPageTitle(this.$t("Cloud Key"));

    this.form.validfrom = this.defaultDate;

    await this.fetchCustomerCloudKey({
      id: this.selectedUser.id,
      cloudKeyId: this.$route.params.id,
    });
  },
};
</script>
